import AddIcon from "@mui/icons-material/Add";
import ClearIcon from "@mui/icons-material/Clear";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Chip,
  Container,
  Fab,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import LazyTable, {
  DEFAULT_ROWS_PER_PAGE,
  TableCellType,
  TableSortType,
} from "components/LazyTable";
import OpenLinkInNewTabContextMenuItem from "components/OpenInNewTabContextMenuItem";
import SelectNotificationChannel from "components/SelectNotificationChannel";
import SelectNotificationStatus from "components/SelectNotificationStatus";
import { channelChipColor } from "constants/NotificationChannelConstants";
import useGlobalContext from "hooks/globalData";
import useDebounce from "hooks/useDebounce";
import useListTitle from "hooks/useListTitle";
import { useEffect, useState } from "react";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQuery } from "react-query";
import { useNavigate } from "react-router-dom";
import {
  ListRequestOfNotificationConfigurationFilter,
  NotificationConfigurationFilter,
} from "services/KnowfullyAdminClient";
import contextStore from "stores/contextStore";
import persistanceStore from "stores/persistanceStore";

const NotificationConfigurationsList = () => {
  const { formatMessage: t } = useIntl();
  const {
    notificationConfigurationCreateUrl,
    notificationConfigurationDetailsUrl,
    notificationsConfigurationsService,
  } = useGlobalContext();
  const navigate = useNavigate();
  const [order, setOrder] = useState<TableSortType>("asc");
  const [orderBy, setOrderBy] = useState("triggerName");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);

  const searchTerm = persistanceStore.useState(
    (s) => s.notificationConfigSearchTerm,
  );
  const channelIds = persistanceStore.useState(
    (s) => s.notificationConfigChannelIds,
  );
  const statusIds = persistanceStore.useState(
    (s) => s.notificationConfigStatusIds,
  );

  const trimmedSearchTerm = (searchTerm ?? "").trim();
  const debouncedSearchTerm = useDebounce(trimmedSearchTerm, 500);
  const { organizationId } = contextStore.useState();

  useListTitle("notificationConfigurations");

  const { isError, data, isFetching } = useQuery(
    [
      "notificationsConfigurationsService.getNotificationsConfigurations",
      organizationId,
      debouncedSearchTerm,
      channelIds,
      statusIds,
      page,
      rowsPerPage,
      order,
      orderBy,
    ],
    () =>
      notificationsConfigurationsService.getNotificationsConfigurations(
        ListRequestOfNotificationConfigurationFilter.fromJS({
          pageNumber: page,
          pageSize: rowsPerPage,
          sortField: orderBy,
          sortDescending: order === "desc",
          parameters: NotificationConfigurationFilter.fromJS({
            searchText: debouncedSearchTerm,
            notificationRoutingTypeIds:
              channelIds.length > 0 ? channelIds : undefined,
            isEnabled:
              statusIds.length === 1 ? statusIds[0] === "1" : undefined,
          }),
        }),
      ),
    { keepPreviousData: true },
  );

  useEffect(() => {
    setPage(0);
  }, [debouncedSearchTerm, organizationId, channelIds, statusIds]);

  const cells: Array<TableCellType> = [
    {
      accessor: "value",
      align: "left",
      width: "20%",
      type: "node",
      View: ({ value }) => (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {value.triggerName}
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: "text.secondary", fontFamily: "monospace" }}
          >
            {value.triggerEventKey}
          </Typography>
        </Box>
      ),
      label: t({ id: "trigger" }),
      customSortAccessor: "triggerName",
    },
    {
      accessor: "condition",
      align: "left",
      width: "30%",
      type: "node",
      View: ({ value }) => (
        <Typography variant="body2">{value.condition}</Typography>
      ),
      label: t({ id: "condition" }),
      customSortAccessor: "condition",
    },
    {
      accessor: "value",
      align: "left",
      width: "10%",
      type: "node",
      View: ({ value }) => (
        <Chip
          label={value.channel}
          size="small"
          variant="outlined"
          color={channelChipColor(value.routingTypeId)}
        />
      ),
      label: t({ id: "channel" }),
      customSortAccessor: "channel",
    },
    {
      accessor: "templateTitle",
      align: "left",
      width: "30%",
      type: "text",
      label: t({ id: "titleSubject" }),
      customSortAccessor: "templateTitle",
    },
    {
      accessor: "value",
      align: "center",
      width: "10%",
      type: "node",
      View: ({ value }) => (
        <Chip
          label={value.isEnabled ? t({ id: "sent" }) : t({ id: "draft" })}
          size="small"
          variant="outlined"
          color={value.isEnabled ? "success" : "default"}
        />
      ),
      label: t({ id: "statusLabel" }),
      customSortAccessor: "isEnabled",
    },
  ];

  const onClick = (id: string) => {
    navigate(notificationConfigurationDetailsUrl(id));
  };

  const notifications = (data?.data ?? []).map((item) => ({
    ...item,
    id: item.notificationJobRoutingId!,
  }));

  if (isError) {
    return (
      <Alert severity="error">
        <T id="fetchError" />
      </Alert>
    );
  }

  return (
    <Container>
      <Box mb={3} textAlign="center">
        <Fab
          aria-label="add"
          color="primary"
          onClick={() => navigate(notificationConfigurationCreateUrl())}
        >
          <AddIcon />
        </Fab>
      </Box>

      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography>{t({ id: "filtersLabel" })}</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                variant="outlined"
                value={searchTerm ?? ""}
                placeholder={t({ id: "searchPlaceholder" })}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() =>
                          persistanceStore.update((s) => {
                            s.notificationConfigSearchTerm = "";
                          })
                        }
                      >
                        <ClearIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                onChange={(e) => {
                  persistanceStore.update((s) => {
                    s.notificationConfigSearchTerm = e.target.value;
                  });
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <SelectNotificationChannel
                value={channelIds}
                onChange={(ids) =>
                  persistanceStore.update((s) => {
                    s.notificationConfigChannelIds = ids;
                  })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <SelectNotificationStatus
                value={statusIds}
                onChange={(ids) =>
                  persistanceStore.update((s) => {
                    s.notificationConfigStatusIds = ids;
                  })
                }
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      <Box mt={2}>
        <LazyTable
          {...{
            rowCount: data?.totalRecords ?? 0,
            rows: notifications,
            loading: isFetching,
            dense: true,
            title: t({ id: "notificationConfigurations" }),
            cells,
            page,
            setPage,
            order,
            setOrder,
            orderBy,
            setOrderBy,
            rowsPerPage,
            setRowsPerPage,
            onClick,
            contextMenuItems: [
              OpenLinkInNewTabContextMenuItem((id) =>
                notificationConfigurationDetailsUrl(id),
              ),
            ],
          }}
        />
      </Box>
    </Container>
  );
};

export default NotificationConfigurationsList;
